// Мягкий рубеж: спросить оператора, прежде чем отправить необычно крупную сумму.
// Пороги и обоснование — в moneySanity.js.
//
// Врезка в ЕДИНСТВЕННЫЙ request-интерцептор, а не в формы. Денежные поля разбросаны
// по 19 компонентам и общего компонента ввода нет; правка каждого была бы работой
// «не забыть», а не свойством. Здесь же рубеж стоит на последнем общем участке перед
// отправкой и покрывает в том числе формы, которых на момент написания не существует.
import {
  findLargeAmount,
  baseCurrencyCode,
  companyCurrencies,
} from "@/utils/moneySanity";

export const MONEY_DECLINED = "money_confirm_declined";

// Предикат для setErrorMixin: отличить осознанный отказ оператора от сбоя.
export function isMoneyDeclined(error) {
  return !!(error && error.__moneyDeclined);
}

const WRITE_METHODS = ["post", "put", "patch"];

// Тело приходит либо объектом (обычный путь axios), либо уже строкой JSON —
// в проекте есть и такие вызовы. Строку, не являющуюся JSON, молча пропускаем:
// это не денежная форма (FormData, чистый текст).
function parseBody(data) {
  if (!data || typeof data === "string") {
    if (typeof data !== "string") return null;
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (e) {
      return null;
    }
  }
  if (typeof FormData !== "undefined" && data instanceof FormData) return null;
  return typeof data === "object" ? data : null;
}

// ОЧЕРЕДЬ ДИАЛОГОВ. ConfirmDialog в приложении один, и его `open()` перезаписывает
// единственный `resolvePromise`: второй вопрос затирает первый, и у первого запроса
// не вызовутся ни then, ни catch, ни finally — форма залипает в isLoading навсегда,
// без тоста и без Rollbar. `axios.defaults.timeout` не спасает: отсчёт стартует в
// адаптере, а до адаптера дело не доходит.
//
// До этого рубежа все 142 вызова `$confirm` шли из обработчиков клика, и два
// одновременных `open()` были практически недостижимы. Интерцептор — первый, кто
// открывает диалог НЕ по клику, то есть первый, кто способен это устроить: два
// параллельных сохранения формы дают ровно такую пару. Найдено ревью 29.08.2026.
//
// Поэтому вопросы выстраиваются в цепочку: следующий открывается только после того,
// как закрыт предыдущий.
let dialogQueue = Promise.resolve();

function askInTurn(ask) {
  const result = dialogQueue.then(ask, ask);
  // Очередь не должна вставать колом из-за отказа предыдущего вопроса.
  dialogQueue = result.then(
    () => undefined,
    () => undefined
  );
  return result;
}

export function createMoneyConfirmGuard({ getCompany, confirm, translate }) {
  return async function moneyConfirmGuard(config) {
    // Повтор того же запроса не переспрашивает. Anti-scrape ретраит запросы своим
    // интерцептором, и без этой пометки оператор получил бы вопрос дважды подряд —
    // худший вид диалога, тот, который приучают закрывать не читая.
    if (!config || config.__moneyConfirmed) return config;
    if (!WRITE_METHODS.includes(String(config.method || "").toLowerCase())) return config;

    const body = parseBody(config.data);
    if (!body) return config;

    let hit = null;
    try {
      const company = getCompany();
      hit = findLargeAmount(body, {
        currencies: companyCurrencies(company),
        baseCode: baseCurrencyCode(company),
      });
    } catch (e) {
      // FAIL-OPEN. Рубеж совещательный: он существует, чтобы СПРОСИТЬ, а не чтобы
      // не пустить. Сбой самого рубежа не имеет права рубить денежную запись на
      // исправном сервере — иначе цена ошибки в нём выше, чем польза от него.
      return config;
    }
    if (!hit) return config;

    let accepted;
    try {
      const amount = formatAmount(hit.value);
      const message = translate("money.large_amount_text", {
        amount: amount,
        currency: hit.currency || "",
      });
      accepted = await askInTurn(() =>
        confirm({
          title: translate("money.large_amount_title"),
          text: message,
          icon: "mdi-alert-outline",
        })
      );
    } catch (e) {
      // Тот же fail-open: $confirm не поднялся, i18n бросил, диалог сломан — пропускаем.
      // Прежняя редакция здесь падала наружу, и оператор получал генерик-ошибку с
      // красным Rollbar на исправном сервере (ревью 29.08.2026).
      return config;
    }

    // `undefined` от неисправного диалога — это НЕ отказ оператора. Отказом считаем
    // только явное «нет»: иначе сломанный диалог молча отменял бы запись.
    if (accepted === false) {
      // Отменяем запрос до отправки. Маркер нужен обработчику ответов: это не сбой
      // сети и не ошибка сервера, показывать «что-то пошло не так» здесь нельзя —
      // оператор сам только что нажал «нет».
      const cancelled = new Error(MONEY_DECLINED);
      cancelled.__moneyDeclined = true;
      throw cancelled;
    }

    config.__moneyConfirmed = true;
    return config;
  };
}

// Форматирование по локали оператора, а не жёстко по-русски: в приложении 15 локалей
// (ревью 29.08.2026 — прежде было захардкожено "ru-RU", и англоязычный оператор видел
// русское форматирование внутри английской фразы).
function formatAmount(value) {
  const locale =
    (typeof document !== "undefined" && document.documentElement.lang) || undefined;
  try {
    return new Intl.NumberFormat(locale).format(value);
  } catch (e) {
    return String(value);
  }
}
